import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { sanitizeEmailSignatureHtml } from './email-signature-html';

/**
 * Shows signature HTML the way an email client does. The app's global stylesheet (Bootstrap reboot's
 * `table { border-collapse: collapse }`, `img { max-width: 100% }`, `p` margins, border-box sizing) restyles
 * anything rendered through [innerHTML], squashing images and turning rounded table cells into rectangles,
 * so the signature renders in its own document instead. The frame is allow-same-origin WITHOUT allow-scripts:
 * the parent can measure it, nothing inside it runs.
 */
@Component({
    selector: 'vf-email-signature-preview',
    templateUrl: './email-signature-preview.component.html',
    styleUrls: ['./email-signature-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailSignaturePreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
    // Email-client defaults rather than the app's. flow-root keeps edge margins inside <body> so its height is
    // the full signature height; only horizontal overflow scrolls (a wide signature on the mobile preview).
    private static readonly FrameStyle = 'html{overflow-x:auto;overflow-y:hidden}'
        + 'body{display:flow-root;margin:0;padding:0;background:transparent;color:#242424;'
        + 'font-family:Aptos,Calibri,Arial,Helvetica,sans-serif;font-size:11pt}';

    @Input() html = '';
    @Input() label = 'Email signature preview';

    @ViewChild('frame') private frame: ElementRef<HTMLIFrameElement> | undefined;

    frameDocument: SafeHtml | undefined;

    private resizeObserver: ResizeObserver | undefined;
    private observedWidth = 0;
    private fitRequestId = 0;

    constructor(
        private sanitizer: DomSanitizer,
        private renderer: Renderer2,
        private zone: NgZone
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.html) {
            return;
        }

        this.frameDocument = this.sanitizer.bypassSecurityTrustHtml(
            '<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>'
            + EmailSignaturePreviewComponent.FrameStyle + '</style></head><body>'
            + sanitizeEmailSignatureHtml(this.html || '')
            + '</body></html>'
        );
    }

    ngAfterViewInit(): void {
        const frame = this.frame ? this.frame.nativeElement : null;
        if (!frame || typeof ResizeObserver === 'undefined') {
            return;
        }

        // Width changes (Desktop/Mobile preview, window resize) re-wrap the signature, so its height must refit.
        this.zone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => this.onFrameResize(entries));
            this.resizeObserver.observe(frame);
        });
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.fitRequestId) {
            cancelAnimationFrame(this.fitRequestId);
        }
    }

    onFrameLoad(): void {
        this.fitFrameHeight();
    }

    private onFrameResize(entries: ResizeObserverEntry[]): void {
        const width = entries.length ? Math.round(entries[0].contentRect.width) : 0;
        if (width === this.observedWidth) {
            return;
        }

        this.observedWidth = width;
        // Next frame: resizing the observed element inside its own callback trips the ResizeObserver loop error.
        cancelAnimationFrame(this.fitRequestId);
        this.fitRequestId = requestAnimationFrame(() => this.fitFrameHeight());
    }

    private fitFrameHeight(): void {
        const frame = this.frame ? this.frame.nativeElement : null;
        const frameDocument = frame ? frame.contentDocument : null;
        let scrollbarHeight = 0;
        let height = 0;

        if (!frame || !frameDocument || !frameDocument.body || !frameDocument.documentElement) {
            return;
        }

        if (frameDocument.defaultView) {
            scrollbarHeight = Math.max(0, frameDocument.defaultView.innerHeight - frameDocument.documentElement.clientHeight);
        }
        height = Math.ceil(frameDocument.body.getBoundingClientRect().height) + scrollbarHeight;
        this.renderer.setStyle(frame, 'height', height + 'px');
    }
}
